import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSessionState } from "@/hooks/useSessionState";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  MessageSquare,
  Download,
  Image as ImageIcon,
  Smartphone,
  Monitor,
  Globe,
  Eye,
  RefreshCw,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  getBetaTestingFeedback, 
  type UserFeedbackFilters,
  type PaginationParams,
  type UserFeedbackResponse 
} from "@/api/beta-testing";
import type { UserFeedbackWithUser } from "@/types/beta-testing";

const UserFeedbackList = () => {
  const { toast } = useToast();
  const [selectedFeedback, setSelectedFeedback] = useState<UserFeedbackWithUser | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useSessionState("userFeedbackList.searchTerm", "");
  const [contextFilter, setContextFilter] = useSessionState("userFeedbackList.contextFilter", "all");
  const [platformFilter, setPlatformFilter] = useSessionState("userFeedbackList.platformFilter", "all");
  const [dateFilter, setDateFilter] = useSessionState("userFeedbackList.dateFilter", "all");
  const [imageFilter, setImageFilter] = useSessionState("userFeedbackList.imageFilter", "all");
  const [sortBy, setSortBy] = useSessionState("userFeedbackList.sortBy", "newest");

  // Pagination states
  const [currentPage, setCurrentPage] = useSessionState("userFeedbackList.currentPage", 1);
  const [pageSize, setPageSize] = useSessionState("userFeedbackList.pageSize", 10);

  // Create filter and pagination objects
  const filters: UserFeedbackFilters = {
    search: searchTerm || undefined,
    context: contextFilter,
    platform: platformFilter,
    dateRange: dateFilter,
    hasImages: imageFilter,
    sortBy,
  };

  const pagination: PaginationParams = {
    page: currentPage,
    pageSize,
  };

  // Fetch user feedback with server-side filtering
  const { data: feedbackResponse, isLoading, refetch } = useQuery({
    queryKey: ["userFeedback", pagination, filters],
    queryFn: () => getBetaTestingFeedback(pagination, filters),
  });

  // Fetch total stats (without filters for overall numbers)
  const { data: totalStatsResponse, refetch: refetchStats } = useQuery({
    queryKey: ["userFeedbackTotalStats"],
    queryFn: () => getBetaTestingFeedback({ page: 1, pageSize: 1000 }, {}),
  });

  // Extract data from response
  const feedback = feedbackResponse?.data || [];
  const totalItems = feedbackResponse?.total || 0;
  const totalPages = feedbackResponse?.totalPages || 0;

  // Reset page when filters change
  const resetPage = () => setCurrentPage(1);

  // Pagination helpers
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const exportFeedback = async () => {
    try {
      // Fetch all data for export (without pagination)
      const allDataResponse = await getBetaTestingFeedback(
        { page: 1, pageSize: 1000 }, // Large page size to get all data
        filters
      );
      
      if (!allDataResponse.data || allDataResponse.data.length === 0) {
        toast({
          title: "No Data",
          description: "No feedback found to export.",
          variant: "destructive",
        });
        return;
      }

      const feedbackData = allDataResponse.data.map((item) => ({
        email: item.user.email,
        name: item.user.name || "No Name",
        context: item.context,
        feedback_date: new Date(item.submitted_at).toLocaleDateString(),
        feedback: item.feedback_text,
        app_version: item.app_version || "N/A",
        platform: item.platform || "N/A",
        has_images: item.image_urls && item.image_urls.length > 0 ? "Yes" : "No",
        image_count: item.image_urls ? item.image_urls.length : 0,
      }));

      const csvContent = [
        [
          "Email",
          "Name", 
          "Context",
          "Feedback Date",
          "Feedback",
          "App Version",
          "Platform",
          "Has Images",
          "Image Count",
        ],
        ...feedbackData.map((row) => [
          row.email,
          row.name,
          row.context,
          row.feedback_date,
          row.feedback,
          row.app_version,
          row.platform,
          row.has_images,
          row.image_count.toString(),
        ]),
      ]
        .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `user-feedback-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Feedback Exported",
        description: `Successfully exported ${feedbackData.length} feedback entries as CSV.`,
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        title: "Export Failed", 
        description: "Failed to export feedback. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRefresh = () => {
    refetch();
    refetchStats();
  };

  const handleViewDetails = (feedbackItem: UserFeedbackWithUser) => {
    setSelectedFeedback(feedbackItem);
    setIsDialogOpen(true);
  };

  const getPlatformIcon = (platform?: string) => {
    if (!platform) return <Globe className="h-4 w-4" />;

    const platformLower = platform.toLowerCase();
    if (
      platformLower.includes("ios") ||
      platformLower.includes("iphone") ||
      platformLower.includes("ipad")
    ) {
      return <Smartphone className="h-4 w-4" />;
    } else if (platformLower.includes("android")) {
      return <Smartphone className="h-4 w-4" />;
    } else if (
      platformLower.includes("web") ||
      platformLower.includes("browser")
    ) {
      return <Monitor className="h-4 w-4" />;
    }
    return <Globe className="h-4 w-4" />;
  };

  if (isLoading) {
    return (
      <AdminLayout title="User Feedback">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="User Feedback">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">User Feedback</h1>
            <p className="text-muted-foreground">
              All user feedback across different contexts
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={exportFeedback}
              disabled={isLoading || totalItems === 0}
              variant="outline"
              size="sm"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Filtered
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Feedback
              </CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalStatsResponse?.total || 0}</div>
              <p className="text-xs text-muted-foreground">All submitted</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Filtered Results
              </CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalItems}</div>
              <p className="text-xs text-muted-foreground">Match current filters</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                With Images
              </CardTitle>
              <ImageIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {totalStatsResponse?.data?.filter(f => f.image_urls && f.image_urls.length > 0).length || 0}
              </div>
              <p className="text-xs text-muted-foreground">Have attachments</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Search & Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* Search */}
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="search">Search</Label>
                <Input
                  id="search"
                  placeholder="Search by user, email, feedback, version..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    resetPage();
                  }}
                />
              </div>

              {/* Context Filter */}
              <div className="space-y-2">
                <Label htmlFor="context">Context</Label>
                <Select
                  value={contextFilter}
                  onValueChange={(val) => {
                    setContextFilter(val);
                    resetPage();
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All contexts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All contexts</SelectItem>
                    <SelectItem value="beta_testing">Beta Testing</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="bug_report">Bug Report</SelectItem>
                    <SelectItem value="feature_request">Feature Request</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Platform Filter */}
              <div className="space-y-2">
                <Label htmlFor="platform">Platform</Label>
                <Select
                  value={platformFilter}
                  onValueChange={(val) => {
                    setPlatformFilter(val);
                    resetPage();
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All platforms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All platforms</SelectItem>
                    <SelectItem value="iOS">iOS</SelectItem>
                    <SelectItem value="Android">Android</SelectItem>
                    <SelectItem value="Web">Web</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date Filter */}
              <div className="space-y-2">
                <Label htmlFor="date">Date Range</Label>
                <Select
                  value={dateFilter}
                  onValueChange={(val) => {
                    setDateFilter(val);
                    resetPage();
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All time</SelectItem>
                    <SelectItem value="7days">Last 7 days</SelectItem>
                    <SelectItem value="30days">Last 30 days</SelectItem>
                    <SelectItem value="3months">Last 3 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Image Filter */}
              <div className="space-y-2">
                <Label htmlFor="images">Images</Label>
                <Select
                  value={imageFilter}
                  onValueChange={(val) => {
                    setImageFilter(val);
                    resetPage();
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="yes">With images</SelectItem>
                    <SelectItem value="no">No images</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Sort and Clear */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="flex items-center gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sort">Sort by</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest first</SelectItem>
                      <SelectItem value="oldest">Oldest first</SelectItem>
                      <SelectItem value="name_asc">Name A-Z</SelectItem>
                      <SelectItem value="name_desc">Name Z-A</SelectItem>
                      <SelectItem value="context">Context</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setContextFilter("all");
                  setPlatformFilter("all");
                  setDateFilter("all");
                  setImageFilter("all");
                  setSortBy("newest");
                  resetPage();
                }}
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Feedback Table */}
        <Card>
          <CardHeader>
            <CardTitle>All User Feedback</CardTitle>
            <CardDescription>
              Feedback submitted by users across different contexts
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin" />
              </div>
            ) : feedback && feedback.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Context</TableHead>
                      <TableHead>Feedback</TableHead>
                      <TableHead>Platform</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Images</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feedback.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                            {item.user.avatar_url ? (
                              <img
                                src={item.user.avatar_url}
                                alt={item.user.name || item.user.email}
                                className="h-8 w-8 rounded-full"
                              />
                            ) : (
                              <span className="text-sm font-medium">
                                {(item.user.name || item.user.email)
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div>
                            <div className="font-medium">
                              {item.user.name || "No Name"}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {item.user.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {item.context.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="text-sm line-clamp-3">
                            {item.feedback_text}
                          </p>
                          {item.app_version && (
                            <p className="text-xs text-muted-foreground mt-1">
                              v{item.app_version}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          {getPlatformIcon(item.platform)}
                          <span className="text-sm">
                            {item.platform || "Unknown"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {new Date(item.submitted_at).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(item.submitted_at).toLocaleTimeString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.image_urls && item.image_urls.length > 0 ? (
                          <div className="flex items-center space-x-1">
                            <ImageIcon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              {item.image_urls.length} image
                              {item.image_urls.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            No images
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(item)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-4 border-t border-border">
                    <div className="text-sm text-muted-foreground">
                      Showing{" "}
                      <span className="font-medium">
                        {totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(currentPage * pageSize, totalItems)}
                      </span>{" "}
                      of <span className="font-medium">{totalItems}</span>{" "}
                      feedback entries
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center">
                        <Button
                          className="mr-1 px-2"
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(1)}
                          disabled={currentPage === 1}
                        >
                          First
                        </Button>

                        <Button
                          className="mr-1 px-2"
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>

                        {getPageNumbers().map((pageNum) => (
                          <Button
                            key={pageNum}
                            className="mx-0.5 w-8 h-8 p-0"
                            variant={pageNum === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => goToPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        ))}

                        <Button
                          className="ml-1 px-2"
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>

                        <Button
                          className="ml-1 px-2"
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(totalPages)}
                          disabled={currentPage === totalPages}
                        >
                          Last
                        </Button>
                      </div>

                      <select
                        className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          resetPage();
                        }}
                      >
                        {[10, 20, 30, 50].map((size) => (
                          <option key={size} value={size}>
                            {size} per page
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {searchTerm || contextFilter !== "all" || platformFilter !== "all" || 
                   dateFilter !== "all" || imageFilter !== "all"
                    ? "No feedback found matching your filters."
                    : "No feedback found."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Feedback Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Feedback Details</DialogTitle>
          </DialogHeader>
          {selectedFeedback && (
            <ScrollArea className="max-h-[600px]">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">User Information</h4>
                    <div className="space-y-1 text-sm">
                      <div>
                        <strong>Name:</strong>{" "}
                        {selectedFeedback.user.name || "No Name"}
                      </div>
                      <div>
                        <strong>Email:</strong> {selectedFeedback.user.email}
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Context & Platform</h4>
                    <div className="space-y-1 text-sm">
                      <div>
                        <strong>Context:</strong>{" "}
                        <Badge variant="outline">
                          {selectedFeedback.context.replace("_", " ")}
                        </Badge>
                      </div>
                      <div>
                        <strong>Platform:</strong> {selectedFeedback.platform || "Unknown"}
                      </div>
                      {selectedFeedback.app_version && (
                        <div>
                          <strong>App Version:</strong> v{selectedFeedback.app_version}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold mb-2">Feedback</h4>
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md text-sm leading-relaxed">
                    {selectedFeedback.feedback_text}
                  </div>
                </div>

                {selectedFeedback.image_urls && selectedFeedback.image_urls.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-semibold mb-2">
                        Images ({selectedFeedback.image_urls.length})
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {selectedFeedback.image_urls.map((imageUrl, index) => (
                          <div 
                            key={index} 
                            className="relative group cursor-pointer"
                            onClick={() => {
                              console.log("Opening image:", imageUrl);
                              window.open(imageUrl, '_blank', 'noopener,noreferrer');
                            }}
                          >
                            <img
                              src={imageUrl}
                              alt={`Feedback image ${index + 1}`}
                              className="w-full h-32 object-cover rounded-md border hover:opacity-80 transition-opacity"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-md flex items-center justify-center">
                              <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Click on any image to view full size
                      </p>
                    </div>
                  </>
                )}

                <Separator />

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Submitted</div>
                    <div>
                      {new Date(selectedFeedback.submitted_at).toLocaleDateString()} at{" "}
                      {new Date(selectedFeedback.submitted_at).toLocaleTimeString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Feedback ID</div>
                    <div className="font-mono text-xs">{selectedFeedback.id}</div>
                  </div>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default UserFeedbackList;